import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore'; // Corrected imports
import { db } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../contexts/NotificationContext';
import { XCircle, Loader, Goal, Award } from 'lucide-react';
import ProgressRing from '../components/common/ProgressRing'; // New import for ProgressRing
import { getWeekId } from '../components/utils'; // Import getWeekId helper

const CommunityGoalsPage = () => {
  const { user } = useAuth(); // Keeping user for potential future use or context
  const { setNotification } = useNotification();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentWeekId = getWeekId(new Date()); // Use getWeekId from utils

  useEffect(() => {
    // Fetch users who have set a weekly goal for the current week
    const q = query(
      collection(db, 'users'),
      where('weeklyGoal', '>', 0), // Only users with a set weekly goal
      // No orderBy('weeklyGoalSetAt', 'desc') needed here if we filter by currentWeekId below
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      let fetchedUsers = snapshot.docs.map(doc => {
        const userData = doc.data();
        // Ensure weeklyGoalSetAt is a Date object for filtering
        return {
          id: doc.id,
          ...userData,
          weeklyGoalSetAt: userData.weeklyGoalSetAt?.toDate ? userData.weeklyGoalSetAt.toDate() : userData.weeklyGoalSetAt,
        };
      });

      // Filter users whose goal was set for the current week
      const currentWeekGoals = fetchedUsers.filter(u => 
        u.weeklyGoal > 0 && u.weeklyGoalSetAt && getWeekId(u.weeklyGoalSetAt) === currentWeekId
      );

      // Sort by achievement rate, then by currentWeeklyStudyTime, then by weeklyGoal
      const sortedGoals = currentWeekGoals.sort((a, b) => {
        const achievementA = a.weeklyGoal > 0 ? (a.weeklyStudyTime / a.weeklyGoal) : 0;
        const achievementB = b.weeklyGoal > 0 ? (b.weeklyStudyTime / b.weeklyGoal) : 0;
        
        if (achievementA !== achievementB) {
          return achievementB - achievementA; // Higher achievement first
        }
        if (a.weeklyStudyTime !== b.weeklyStudyTime) {
          return b.weeklyStudyTime - a.weeklyStudyTime; // Higher current study time next
        }
        return b.weeklyGoal - a.weeklyGoal; // Higher target goal next
      });


      // Map users data to a "goal" format for renderGoalCard
      const goalsData = sortedGoals.map(userData => ({
        userId: userData.id,
        userName: userData.name || userData.displayName || '名無し',
        photoURL: userData.photoURL || null,
        targetStudyTime: userData.weeklyGoal || 0, // Target weekly goal from users doc (in minutes)
        currentWeeklyStudyTime: userData.weeklyStudyTime || 0, // Current actual weekly study time from users doc (in minutes)
        weeklyGoalSetAt: userData.weeklyGoalSetAt, // Pass weeklyGoalSetAt for display
      }));
      
      setGoals(goalsData);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching community goals from users:", error);
      setNotification({ message: '目標の取得中にエラーが発生しました。', type: 'error' });
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentWeekId, setNotification]);


  const renderGoalCard = useCallback((goal) => {
    const targetHours = (goal.targetStudyTime / 60).toFixed(1);
    const currentActualHours = (goal.currentWeeklyStudyTime / 60).toFixed(1);
    const achievementRate = goal.targetStudyTime > 0 
      ? Math.min(100, (goal.currentWeeklyStudyTime / goal.targetStudyTime) * 100)
      : 0;

    return (
      <div key={goal.userId} className="bg-white dark:bg-gray-800 shadow-md rounded-xl p-4 border border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4">
        
        <div className="flex-shrink-0 flex items-center justify-center relative w-24 h-24">
            <ProgressRing
                radius={40}
                stroke={8}
                progress={achievementRate}
                strokeColor={
                    achievementRate >= 100 ? "#22c55e" : // green-500
                    achievementRate >= 50 ? "#3b82f6" :  // blue-500
                    "#ef4444"                           // red-500
                }
                bgColor="#e6e6e6" // A light gray for background circle, similar to original default
            />
            <div className="absolute text-xl font-bold text-gray-900 dark:text-white">
                {achievementRate.toFixed(0)}%
            </div>
        </div>

        <div className="flex-grow text-center sm:text-left">
            <div className="flex items-center justify-center sm:justify-start mb-2">
                {goal.photoURL ? (
                    <img src={goal.photoURL} alt={goal.userName} className="w-10 h-10 rounded-full mr-3 border-2 border-blue-400" />
                ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center mr-3 border-2 border-blue-400">
                        <Goal size={24} className="text-gray-500 dark:text-gray-400" />
                    </div>
                )}
                <div>
                    <p className="font-semibold text-lg text-gray-900 dark:text-white">{goal.userName}</p>
                    {/* Display goal set date */}
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      （目標設定日: {goal.weeklyGoalSetAt?.toLocaleDateString() || '不明'}）
                    </p>
                </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-100 dark:border-gray-600">
                <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                        <Goal size={16} className="mr-2 text-blue-500" /> 目標勉強時間:
                    </span>
                    <span className="text-xl font-bold text-blue-600 dark:text-blue-400">{targetHours} 時間</span>
                </div>
                <div className="flex justify-between items-center pt-2 mt-2 border-t border-gray-200 dark:border-gray-600">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                        <Award size={16} className="mr-2 text-green-500" /> 現在の学習時間:
                    </span>
                    <span className="text-xl font-bold text-green-600 dark:text-green-400">{currentActualHours} 時間</span>
                </div>
            </div>
        </div>
      </div>
    );
  }, [currentWeekId]); // Add currentWeekId to useCallback dependencies


  return (
    <div className="container mx-auto p-4 sm:p-6 lg:p-8 max-w-2xl">
      <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-6 text-center">
        <Goal className="inline-block mr-2 text-blue-500" size={32} />
        みんなの今週の目標
      </h2>

      <div className="space-y-4">
        {loading ? (
          <div className="flex justify-center items-center h-48">
            <Loader className="animate-spin text-blue-500" size={40} />
          </div>
        ) : goals.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 text-center text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
            <XCircle className="mx-auto mb-3" size={40} />
            <p className="text-lg font-semibold">まだ今週の目標を設定しているユーザーはいません。</p>
            <p className="text-sm">誰かが目標を設定するのを待ちましょう！</p>
            {user && (!user.weeklyGoal || user.weeklyGoal <= 0) && (
                <p className="text-sm text-red-500 text-center mt-2">※ あなたの目標はまだ設定されていません。（マイページの「目標設定」から）</p>
            )}
          </div>
        ) : (
          goals.map(renderGoalCard)
        )}
      </div>
    </div>
  );
};

export default CommunityGoalsPage;